import { ChargingModelValues } from '@/api/generated/enums';
import { DatePicker } from '@/components/DatePicker';
import { controlClass, Field, selectClass } from '@/components/Field';
import { Card, SectionTitle } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';

import type { Plan } from './api';
import {
  defaultGraceDays,
  isNegotiatedDraft,
  listPriceFor,
  maxGraceDays,
  modelLabels,
  periodLabels,
  periodsFor,
  type TermsDraft,
} from './billing';

const modelHints: Record<(typeof ChargingModelValues)[number], string> = {
  Subscription: 'A fixed amount every month, quarter or year.',
  LicenceWithAmc: 'A one-time licence that covers the first year, then a yearly AMC.',
  Pilot: 'Free until a date you choose, then grace, then read-only.',
};

/**
 * The fields of a company's terms, written once for the two pages that set them — Set terms, and
 * New company, which sets the first. Two copies of a form drift, and the neglected one is the one a
 * person meets least often, which for terms is the first. The amounts are left blank to follow the
 * package's list price; a figure that differs asks why.
 */
export function TermsFields({
  draft,
  onChange,
  plans,
  packageFooter,
}: {
  draft: TermsDraft;
  onChange: (update: (draft: TermsDraft) => TermsDraft) => void;
  plans: readonly Plan[];
  /** Rendered at the foot of the package card — the new company page's "includes" line. */
  packageFooter?: React.ReactNode;
}) {
  const plan = plans.find((p) => p.code === draft.planCode);
  const list = listPriceFor(plan, draft);
  const pilot = draft.chargingModel === 'Pilot';
  const licence = draft.chargingModel === 'LicenceWithAmc';
  const negotiated = isNegotiatedDraft(draft, list);

  const set =
    <K extends keyof TermsDraft>(key: K) =>
    (value: TermsDraft[K]) =>
      onChange((d) => ({ ...d, [key]: value }));

  // A model brings its own periods; the period carries over where it still applies.
  const chooseModel = (model: TermsDraft['chargingModel']) =>
    onChange((d) => {
      const periods = periodsFor(model);
      return {
        ...d,
        chargingModel: model,
        billingPeriod: periods.includes(d.billingPeriod)
          ? d.billingPeriod
          : (periods[0] ?? 'Monthly'),
      };
    });

  const amount = (
    key: 'recurring' | 'oneTime',
    label: string,
    listed: number | null | undefined,
  ) => (
    <Field
      label={label}
      hint={
        listed != null
          ? `Blank takes the list price, ₹${formatAmount(listed)}.`
          : 'No list price — type the amount agreed.'
      }
      required={listed == null}
    >
      <div className="relative">
        <span className="text-default pointer-events-none absolute inset-y-0 start-3 flex items-center">
          ₹
        </span>
        <input
          className={`${controlClass} ps-7 text-end`}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          placeholder={listed != null ? String(listed) : ''}
          value={draft[key]}
          onChange={(event) => set(key)(event.target.value)}
          onWheel={(event) => event.currentTarget.blur()}
        />
      </div>
    </Field>
  );

  return (
    <>
      <Card>
        <SectionTitle icon="icon icon-package" hue="purple">
          Package and charging
        </SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Package" required>
            <select
              className={selectClass}
              value={draft.planCode}
              onChange={(event) => set('planCode')(event.target.value)}
            >
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="How it is charged" hint={modelHints[draft.chargingModel]} required>
            <select
              className={selectClass}
              value={draft.chargingModel}
              onChange={(event) => chooseModel(event.target.value as TermsDraft['chargingModel'])}
            >
              {ChargingModelValues.map((model) => (
                <option key={model} value={model}>
                  {modelLabels[model]}
                </option>
              ))}
            </select>
          </Field>
          {draft.chargingModel === 'Subscription' && (
            <Field label="Every" required>
              <select
                className={selectClass}
                value={draft.billingPeriod}
                onChange={(event) =>
                  set('billingPeriod')(event.target.value as TermsDraft['billingPeriod'])
                }
              >
                {periodsFor('Subscription').map((period) => (
                  <option key={period} value={period}>
                    {periodLabels[period]}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {pilot && (
            <Field label="Pilot ends on" hint="The last free day." required>
              <DatePicker
                value={toDayjs(draft.pilotEndsOn) ?? null}
                onChange={(value) => set('pilotEndsOn')(toIsoDate(value) ?? '')}
                className="w-full"
              />
            </Field>
          )}
        </div>
        {packageFooter}
      </Card>

      {!pilot && (
        <Card>
          <SectionTitle icon="icon icon-badge-indian-rupee" hue="teal">
            Price
          </SectionTitle>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {licence && amount('oneTime', 'Licence fee', list?.oneTimeAmount)}
            {amount(
              'recurring',
              licence ? 'AMC, every year' : `${periodLabels[draft.billingPeriod]} amount`,
              list?.recurringAmount,
            )}
            {(negotiated || draft.note) && (
              <div className="sm:col-span-2">
                <Field
                  label="Why this price"
                  hint="Kept on the terms — what is read back when somebody asks why this company pays less."
                  required={negotiated}
                >
                  <input
                    className={controlClass}
                    type="text"
                    maxLength={512}
                    value={draft.note}
                    onChange={(event) => set('note')(event.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <SectionTitle icon="icon icon-hourglass" hue="indigo">
          Grace
        </SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Days of grace"
            hint={`Full access this long after it falls due, then read-only. Blank takes ${defaultGraceDays(draft.chargingModel)} days, the default for ${modelLabels[draft.chargingModel]}.`}
          >
            <input
              className={controlClass}
              type="number"
              inputMode="numeric"
              min={0}
              max={maxGraceDays}
              placeholder={String(defaultGraceDays(draft.chargingModel))}
              value={draft.grace}
              onChange={(event) => set('grace')(event.target.value)}
              onWheel={(event) => event.currentTarget.blur()}
            />
          </Field>
        </div>
      </Card>
    </>
  );
}
